import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AppPreferencesService } from '../../../services/app-preferences';
import { createManageOutageFormValue } from './manage-outage.data';
import {
  ManageOutageEntityItem,
  ManageOutageItem,
  ManageOutagePagination,
  ManageOutageSubstationItem,
} from './manage-outage.interfaces';

type ManageOutageModalMode = 'create' | 'edit' | 'view';

const OUTAGE_TYPE_OPTIONS = [
  { label: 'Automatic', value: 'Automatic' },
  { label: 'Transient', value: 'Transient' },
];

@Component({
  selector: 'app-manage-outage-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-outage.html',
  styleUrl: './manage-outage.scss',
})
export class ManageOutagePage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filterForm: UntypedFormGroup;
  protected readonly pageSizeControl: FormControl;
  protected readonly outageForm: UntypedFormGroup;
  protected readonly outageTypeOptions = OUTAGE_TYPE_OPTIONS;
  protected readonly pageSizeOptions = [15, 25, 50, 100];

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isEntityLoading = false;
  protected isSubstationLoading = false;
  protected outageModalOpen = false;
  protected deleteModalOpen = false;
  protected modalMode: ManageOutageModalMode = 'create';
  protected selectedOutage: ManageOutageItem | null = null;
  protected formSubmitted = false;

  protected outages: ManageOutageItem[] = [];
  protected entities: ManageOutageEntityItem[] = [];
  protected substations: ManageOutageSubstationItem[] = [];
  protected pagination: ManageOutagePagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.outageForm = this.fb.group({
      mstr_entity_id: [null, Validators.required],
      mstr_substation_id: [null, Validators.required],
      outage_type: ['', [Validators.required, Validators.maxLength(20)]],
      outage_start: ['', Validators.required],
      expected_end: ['', [this.dateAfterStartValidator('expected_end')]],
      actual_revival: ['', [this.dateAfterStartValidator('actual_revival')]],
      reason: [''],
    });
  }

  ngOnInit(): void {
    this.loadEntities();
    this.loadOutages(1);

    this.outageForm.get('mstr_entity_id')?.valueChanges
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const entityId = this.normalizeNumericValue(value);
        this.outageForm.patchValue({ mstr_substation_id: null }, { emitEvent: false });
        this.substations = [];

        if (entityId !== null) {
          this.loadSubstations(entityId);
        }
      });

    this.outageForm.get('outage_start')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.outageForm.get('expected_end')?.updateValueAndValidity({ emitEvent: false });
        this.outageForm.get('actual_revival')?.updateValueAndValidity({ emitEvent: false });
      });

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadOutages(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadOutages(1);
  }

  protected openAddOutageModal(): void {
    this.modalMode = 'create';
    this.selectedOutage = null;
    this.outageForm.enable({ emitEvent: false });
    this.outageForm.reset(createManageOutageFormValue(), { emitEvent: false });
    this.substations = [];
    this.formSubmitted = false;
    this.outageModalOpen = true;
  }

  protected openEditOutageModal(outage: ManageOutageItem): void {
    this.modalMode = 'edit';
    this.selectedOutage = outage;
    this.outageForm.enable({ emitEvent: false });
    this.outageForm.reset({
      mstr_entity_id: outage.mstr_entity_id,
      mstr_substation_id: outage.mstr_substation_id,
      outage_type: outage.outage_type ?? '',
      outage_start: outage.outage_start ?? '',
      expected_end: outage.expected_end ?? '',
      actual_revival: outage.actual_revival ?? '',
      reason: outage.reason ?? '',
    }, { emitEvent: false });
    this.formSubmitted = false;
    this.outageModalOpen = true;
    this.loadSubstations(outage.mstr_entity_id, outage.mstr_substation_id);
  }

  protected openViewOutageModal(outage: ManageOutageItem): void {
    this.modalMode = 'view';
    this.selectedOutage = outage;
    this.outageForm.enable({ emitEvent: false });
    this.outageForm.reset({
      mstr_entity_id: outage.mstr_entity_id,
      mstr_substation_id: outage.mstr_substation_id,
      outage_type: outage.outage_type ?? '',
      outage_start: outage.outage_start ?? '',
      expected_end: outage.expected_end ?? '',
      actual_revival: outage.actual_revival ?? '',
      reason: outage.reason ?? '',
    }, { emitEvent: false });
    this.formSubmitted = false;
    this.outageModalOpen = true;
    this.outageForm.disable({ emitEvent: false });
    this.loadSubstations(outage.mstr_entity_id, outage.mstr_substation_id);
  }

  protected openDeleteOutageModal(outage: ManageOutageItem): void {
    this.selectedOutage = outage;
    this.deleteModalOpen = true;
  }

  protected closeOutageModal(): void {
    this.outageModalOpen = false;
    this.selectedOutage = null;
    this.modalMode = 'create';
    this.outageForm.enable({ emitEvent: false });
    this.substations = [];
    this.outageForm.reset(createManageOutageFormValue(), { emitEvent: false });
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedOutage = null;
  }

  protected saveOutage(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.outageForm.invalid) {
      this.formSubmitted = true;
      this.outageForm.markAllAsTouched();
      return;
    }

    const payload = this.buildOutagePayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_OUTAGE : AuthEndPoints.EDIT_OUTAGE;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeOutageModal();
          this.loadOutages(this.pagination.page_no);
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedOutage) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_OUTAGE, ApiMethod.DELETE, { id: this.selectedOutage.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadOutages(this.pagination.page_no);
        },
      });
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadOutages(pageNo);
  }

  protected trackByOutageId(_: number, outage: ManageOutageItem): number {
    return outage.id;
  }

  protected trackByEntityId(_: number, entity: ManageOutageEntityItem): number {
    return entity.id;
  }

  protected trackBySubstationId(_: number, substation: ManageOutageSubstationItem): number {
    return substation.id;
  }

  protected getEntityName(entityId: number | null): string {
    if (entityId === null) {
      return '-';
    }

    return this.entities.find((entity) => entity.id === entityId)?.entity_name ?? '-';
  }

  protected getSubstationName(substationId: number | null): string {
    if (substationId === null) {
      return '-';
    }

    return this.substations.find((substation) => substation.id === substationId)?.station_name ?? '-';
  }

  protected formatDateTime(value: string | null): string {
    if (!value) {
      return '-';
    }

    return this.preferencesSvc.formatDateTime(value);
  }

  protected formatOutageDate(adValue: string | null, bsValue: string | null): string {
    if (this.preferencesSvc.showBsDate) {
      if (!adValue) {
        return bsValue || '-';
      }

      const timePart = this.extractTimePart(adValue);
      if (bsValue) {
        return timePart ? `${bsValue} ${timePart}` : bsValue;
      }

      return this.formatDateTime(adValue);
    }

    if (!adValue) {
      return '-';
    }

    return this.formatDateTime(adValue);
  }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.outageForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected isDateFieldInvalid(fieldName: 'expected_end' | 'actual_revival'): boolean {
    const control = this.outageForm.get(fieldName);
    return Boolean(control?.hasError('dateAfterStart') && (control.touched || this.formSubmitted));
  }

  protected getOutageMinDateTime(): string | null {
    const value = this.outageForm.get('outage_start')?.value;
    return typeof value === 'string' && value ? value : null;
  }

  protected getDurationText(item: ManageOutageItem): string {
    return item.duration_display || '--/--/--';
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return 'No records';
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `Showing ${start} to ${end} of ${this.pagination.total_records} results`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);

    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    if (windowStart > 2) {
      pages.push(-1);
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      addPage(page);
    }

    if (windowEnd < totalPages - 1) {
      pages.push(-1);
    }

    addPage(totalPages);

    return pages;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected isSelectedEntity(entityId: number | null): boolean {
    return entityId !== null && this.outageForm.get('mstr_entity_id')?.value === entityId;
  }

  private extractTimePart(value: string): string | null {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const [datePart, timePart] = normalized.split(/[T\s]/, 2);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart) || !timePart) {
      return null;
    }

    const timeOnlyPattern = /^(\d{2}):(\d{2})(?::(\d{2}))?/;
    const timeOnlyMatch = timeOnlyPattern.exec(timePart);
    if (timeOnlyMatch) {
      const hours = timeOnlyMatch[1];
      const minutes = timeOnlyMatch[2];
      const seconds = timeOnlyMatch[3] ?? '00';
      return `${hours}:${minutes}:${seconds}`;
    }

    return null;
  }

  private loadEntities(): void {
    this.isEntityLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_ENTITIES_LIST, ApiMethod.GET, '')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isEntityLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.entities = this.extractListResponse(response, 'entities').map((item) => this.mapEntityItem(item));
        },
        error: () => {
          this.entities = [];
        },
      });
  }

  private loadSubstations(entityId: number, selectedSubstationId: number | null = null): void {
    this.isSubstationLoading = true;

    this.httpSvc
      .requestCall(
        this.buildQueryRoute(AuthEndPoints.GET_SUBSTATIONS_LIST, { entity_id: entityId }),
        ApiMethod.GET,
        '',
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubstationLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.substations = this.extractListResponse(response, 'substations').map((item) => this.mapSubstationItem(item));

          if (selectedSubstationId !== null) {
            this.outageForm.patchValue({ mstr_substation_id: selectedSubstationId }, { emitEvent: false });
          }
        },
        error: () => {
          this.substations = [];
        },
      });
  }

  private loadOutages(pageNo: number): void {
    this.isLoading = true;
    this.pagination = {
      ...this.pagination,
      page_no: pageNo,
    };

    const formValue = this.filterForm.getRawValue();
    const params: Record<string, unknown> = {
      page_no: pageNo,
      page_size: formValue.page_size ?? 10,
    };
    const search = String(formValue.search ?? '').trim();
    if (search) {
      params['search'] = search;
    }

    this.httpSvc
      .requestCall(this.buildQueryRoute(AuthEndPoints.GET_OUTAGE_LIST, params), ApiMethod.GET, '')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.finishLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          const value = this.extractValue(response);
          this.outages = this.extractList(value, 'outages').map((item) => this.mapOutageItem(item));
          this.pagination = this.mapPagination(value);
          this.finishLoading();
        },
        error: () => {
          this.outages = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(formValue.page_size ?? 10),
            total_records: 0,
            total_pages: 0,
          };
          this.finishLoading();
        },
      });
  }

  private buildOutagePayload(): Record<string, unknown> {
    const formValue = this.outageForm.getRawValue();
    const normalized = this.normalizeFormValue(formValue);

    if (this.modalMode === 'create') {
      return normalized;
    }

    if (!this.selectedOutage) {
      return {};
    }

    const payload: Record<string, unknown> = {
      id: this.selectedOutage.id,
    };

    for (const [key, value] of Object.entries(normalized)) {
      const selectedValue = this.getObjectValue(this.selectedOutage, key);
      if (value !== selectedValue) {
        payload[key] = value;
      }
    }

    return payload;
  }

  private normalizeFormValue(formValue: Record<string, unknown>): Record<string, unknown> {
    const normalizeText = (value: unknown): string | null => {
      if (typeof value !== 'string') {
        return null;
      }

      const text = value.trim();
      return text || null;
    };

    return {
      mstr_entity_id: this.normalizeNumericValue(formValue['mstr_entity_id']),
      mstr_substation_id: this.normalizeNumericValue(formValue['mstr_substation_id']),
      outage_type: normalizeText(formValue['outage_type']),
      outage_start: normalizeText(formValue['outage_start']),
      expected_end: normalizeText(formValue['expected_end']),
      actual_revival: normalizeText(formValue['actual_revival']),
      reason: normalizeText(formValue['reason']),
    };
  }

  private normalizeNumericValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private extractValue(response: unknown): unknown {
    return this.getObjectValue(response, 'value');
  }

  private extractListResponse(response: unknown, key: 'entities' | 'substations' | 'outages'): unknown[] {
    const value = this.extractValue(response);
    return this.extractList(value, key);
  }

  private extractList(value: unknown, key: 'entities' | 'substations' | 'outages'): unknown[] {
    const candidate = this.getObjectValue(value, key);
    if (!candidate) {
      return [];
    }

    return Array.isArray(candidate) ? candidate : [];
  }

  private mapPagination(value: unknown): ManageOutagePagination {
    const pagination = this.getObjectValue(value, 'pagination');
    return {
      page_no: Number(this.getObjectValue(pagination, 'page_no') ?? 1),
      page_size: Number(this.getObjectValue(pagination, 'page_size') ?? 10),
      total_records: Number(this.getObjectValue(pagination, 'total_records') ?? 0),
      total_pages: Number(this.getObjectValue(pagination, 'total_pages') ?? 0),
    };
  }

  private mapEntityItem(item: unknown): ManageOutageEntityItem {
    return {
      id: Number(this.getObjectValue(item, 'id') ?? 0),
      serial_no: Number(this.getObjectValue(item, 'serial_no') ?? 0),
      entity_name: this.getStringValue(item, 'entity_name') ?? 'Untitled entity',
      entity_type: this.getStringValue(item, 'entity_type'),
    };
  }

  private mapSubstationItem(item: unknown): ManageOutageSubstationItem {
    return {
      id: Number(this.getObjectValue(item, 'id') ?? 0),
      serial_no: Number(this.getObjectValue(item, 'serial_no') ?? 0),
      mstr_entity_id: Number(this.getObjectValue(item, 'mstr_entity_id') ?? 0),
      entity_name: this.getStringValue(item, 'entity_name'),
      entity_type: this.getStringValue(item, 'entity_type'),
      station_name: this.getStringValue(item, 'station_name') ?? 'Untitled substation',
      station_code: this.getStringValue(item, 'station_code'),
      operational_status: this.getStringValue(item, 'operational_status'),
    };
  }

  private mapOutageItem(item: unknown): ManageOutageItem {
    return {
      id: Number(this.getObjectValue(item, 'id') ?? 0),
      serial_no: Number(this.getObjectValue(item, 'serial_no') ?? 0),
      mstr_substation_id: Number(this.getObjectValue(item, 'mstr_substation_id') ?? 0),
      mstr_entity_id: Number(this.getObjectValue(item, 'mstr_entity_id') ?? 0),
      entity_name: this.getStringValue(item, 'entity_name'),
      entity_type: this.getStringValue(item, 'entity_type'),
      station_name: this.getStringValue(item, 'station_name') ?? 'Untitled substation',
      station_code: this.getStringValue(item, 'station_code'),
      outage_type: this.getStringValue(item, 'outage_type'),
      outage_start: this.toInputDateTimeValue(this.getObjectValue(item, 'outage_start')),
      outage_start_bs: this.getStringValue(item, 'outage_start_bs'),
      expected_end: this.toInputDateTimeValue(this.getObjectValue(item, 'expected_end')),
      expected_end_bs: this.getStringValue(item, 'expected_end_bs'),
      actual_revival: this.toInputDateTimeValue(this.getObjectValue(item, 'actual_revival')),
      actual_revival_bs: this.getStringValue(item, 'actual_revival_bs'),
      no_of_hours: this.getNumberValue(item, 'no_of_hours'),
      reason: this.getStringValue(item, 'reason'),
      duration_days: this.getNumberValue(item, 'duration_days'),
      duration_hours: this.getNumberValue(item, 'duration_hours'),
      duration_minutes: this.getNumberValue(item, 'duration_minutes'),
      duration_display: this.getStringValue(item, 'duration_display') ?? '--/--/--',
    };
  }

  private toInputDateTimeValue(value: unknown): string | null {
    if (typeof value !== 'string' || !value) {
      return null;
    }

    if (value.includes('T')) {
      return value.slice(0, 16);
    }

    return value.replace(' ', 'T').slice(0, 16);
  }

  private dateAfterStartValidator(fieldName: 'expected_end' | 'actual_revival'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startValue = this.outageForm?.get('outage_start')?.value;
      const nextValue = control.value;

      if (!startValue || !nextValue) {
        return null;
      }

      const startTime = new Date(startValue).getTime();
      const nextTime = new Date(nextValue).getTime();

      if (Number.isNaN(startTime) || Number.isNaN(nextTime)) {
        return null;
      }

      return nextTime > startTime ? null : { dateAfterStart: fieldName };
    };
  }

  private buildQueryRoute(baseRoute: string, params: Record<string, unknown>): string {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }

      if (typeof value === 'string') {
        query.set(key, value);
        continue;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        query.set(key, value.toString());
      }
    }

    const queryString = query.toString();
    return queryString ? `${baseRoute}?${queryString}` : baseRoute;
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private getObjectValue(value: unknown, key: string): unknown {
    if (!value || typeof value !== 'object') {
      return null;
    }

    for (const [entryKey, entryValue] of Object.entries(value)) {
      if (entryKey === key) {
        return entryValue ?? null;
      }
    }

    return null;
  }

  private getStringValue(value: unknown, key: string): string | null {
    const candidate = this.getObjectValue(value, key);
    return typeof candidate === 'string' ? candidate : null;
  }

  private getNumberValue(value: unknown, key: string): number | null {
    const candidate = this.getObjectValue(value, key);
    if (candidate === null || candidate === undefined || candidate === '') {
      return null;
    }

    const numericValue = Number(candidate);
    return Number.isNaN(numericValue) ? null : numericValue;
  }
}
